/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `formatAttendees` for the Caintra Connect frontend.
 * @module logic/attendance/formatAttendees
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
const formatAttendanceFlag = (attended) => (attended ? "Sí" : "No");

const formatBusinessEventAttendee = (attendee) => ({
  nombre: attendee.nombreUsuario ?? "",
  correoElectronico: attendee.correoElectronicoUsuario ?? "",
  numeroSocioEmpresa: attendee.numeroSocioEmpresa ?? "",
  rfc: attendee.rfcEmpresa ?? "",
  nombreComercial: attendee.nombreEmpresa ?? "",
  nombreEmpresaCompradora: attendee.nombreEmpresaCompradora ?? "",
  nombreRequerimiento: attendee.nombreRequerimiento ?? "",
  fechaEvento: attendee.fechaEvento ?? "",
  asistio: formatAttendanceFlag(attendee.asistio),
});

const formatB2bEventAttendee = (attendee) => ({
  nombre: attendee.nombreUsuario ?? "",
  correoElectronico: attendee.correoElectronicoUsuario ?? "",
  numeroSocioEmpresa: attendee.numeroSocioEmpresa ?? "",
  rfc: attendee.rfcEmpresa ?? "",
  nombreComercial: attendee.nombreComercial ?? "",
  fechaEvento: attendee.fechaEvento ?? "",
  asistio: formatAttendanceFlag(attendee.asistio),
});

const formatBreakfastEventAttendee = (attendee, selectedEvent) => ({
  nombre: attendee.nombreUsuario ?? "",
  correoElectronico: attendee.correoElectronicoUsuario ?? "",
  numeroSocioEmpresa: attendee.numeroSocioEmpresa ?? "",
  rfc: attendee.rfcEmpresa ?? "",
  nombreComercial: attendee.nombreEmpresa ?? "",
  fechaEvento: selectedEvent.fechaEvento ?? "",
  asistio: formatAttendanceFlag(attendee.asistio),
});

const formatDefaultEventAttendee = (attendee) => ({
  nombre: attendee.nombre ?? "",
  correoElectronico: attendee.correoElectronico ?? "",
  telefono: attendee.telefono ?? "",
  nombreComercial: attendee.nombreComercial ?? "",
  razonSocial: attendee.razonSocial ?? "",
  socio: "Sí",
  asistio: formatAttendanceFlag(attendee.asistio),
  rfc: attendee.rfc ?? "",
  sector: attendee.sector ?? "",
  subSector: attendee.subSector ?? "",
  rama: attendee.rama ?? "",
  numeroTotalTrabajadores: attendee.numeroTotalTrabajadores ?? "",
  ambitoOperaciones: attendee.ambitoOperaciones ?? "",
  puesto: attendee.puesto ?? "",
  area: attendee.area ?? "",
  genero: attendee.genero ?? "",
  interesesComerciales: attendee.interesesComerciales ?? "",
  expectativas: attendee.expectativas ?? "",
});

const ATTENDEE_FORMATTERS = {
  BUSINESS_EVENT: (attendees) => attendees.map(formatBusinessEventAttendee),
  B2B_EVENT: (attendees) => attendees.map(formatB2bEventAttendee),
  BREAKFAST_EVENT: (attendees, selectedEvent) =>
    attendees.map((attendee) =>
      formatBreakfastEventAttendee(attendee, selectedEvent),
    ),
};

/**
 * Formats attendee data for CSV export based on event type.
 * @param {Array} attendees - Array of attendee objects
 * @param {Object} selectedEvent - Selected event with tipoEvento
 * @returns {Array}
 */
export const formatAttendees = (attendees, selectedEvent) => {
  const formatter = ATTENDEE_FORMATTERS[selectedEvent.tipoEvento];

  if (formatter) {
    return formatter(attendees, selectedEvent);
  }

  return attendees.map(formatDefaultEventAttendee);
};
